import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import LedgerSection from './component';
import {
  deleteOneTimePayment,
  deletePaymentPlan,
  getIreneDashboard,
  sendNotification,
} from '@rental-manager/actions/requests';
import {
  DEFAULT_ERROR,
  INVITE_RENTER_SUCCESS,
  ONE_TIME_PAYMENT_CANCEL_SUCCESS,
  PAYMENT_PLAN_CANCEL_SUCCESS,
  REMIND_RENTER_SUCCESS,
} from '@rental-manager/constants/NotificationConstants';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import TransactionConstants from '@common/constants/TransactionConstants';
import paths from '@rental-manager/pathsManifest';
import { saveMoveInCostsSettings } from '@rental-manager/actions/OnboardingActions';
import type { EditMoveInCostsInterface } from '@rental-manager/pages/Dashboard/types';
import DismissibleActionConstants from '@common/constants/DismissibleActionConstants';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import { logError } from '@zg-rentals/log-error';

function copyTransactions(transactions) {
  return transactions.map((transaction) => {
    return { ...transaction };
  });
}

export const mapStateToProps = (state): Record<string, any> => {
  const { userEvent, featurePermissions, currentTerm, nextTerm, listingAlias, dismissibleActions } =
    state.payments.landlord.property;

  const { tenantOnBoarded, termEnded, rentTermsKey } = currentTerm;
  const paymentPlanEnabled =
    featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENTAL_PROTECTION_PAYMENT_PLAN].isAllowed;
  const nextPayableScheduleMonth = currentTerm?.nextPayable.scheduleMonth;
  const allTransactions = state.payments.landlord.property.allTransactions;
  const { upcomingTotal, completedTotal, pastDueTotal } = allTransactions;
  const upcomingTransactions = copyTransactions(allTransactions.upcomingTransactions);
  const completedTransactions = copyTransactions(allTransactions.completedTransactions);
  const pastDueTransactions = copyTransactions(allTransactions.pastDueTransactions);
  const unpaidTransactions = [...upcomingTransactions, ...pastDueTransactions];
  const type = FeaturePermissionTypes.RESEND_INVITE;

  unpaidTransactions.forEach((transaction) => {
    const { landlordStatus, rentTermsKey: txRentTermsKey, scheduleMonth } = transaction;
    const { isAllowed, exhausted } = featurePermissions?.[txRentTermsKey]?.[type] || {};

    transaction.showResendInvitation = !tenantOnBoarded && !termEnded && isAllowed && !exhausted;

    transaction.showRemindRenter =
      tenantOnBoarded &&
      !termEnded &&
      landlordStatus === TransactionConstants.STATUS.PAST_DUE &&
      scheduleMonth === nextPayableScheduleMonth;
  });

  completedTransactions.forEach((transaction) => {
    transaction.showPaidOffline = transaction.landlordStatus === TransactionConstants.STATUS.MANUAL;
  });

  return {
    upcomingTransactions,
    completedTransactions,
    pastDueTransactions,
    allTransactions,
    upcomingTotal,
    completedTotal,
    pastDueTotal,
    // TODO: Find a better way to use this
    userEvent,
    featurePermissions,
    currentTerm,
    nextTerm,
    listingAlias,
    dismissibleActions,
    paymentPlanEnabled,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  cancelOneTimePayment: async ({
    otpKey,
    tenantFirstName,
    paymentType,
    paymentPlanInstallmentTitle,
  }: Record<string, string>): Promise<any> => {
    try {
      const isOtpTypePaymentPlan = paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN;
      await dispatch(closeNotification());
      const action = isOtpTypePaymentPlan
        ? await dispatch(deletePaymentPlan({ propertyId, requestKey: otpKey }))
        : await dispatch(deleteOneTimePayment({ propertyId, groupKey: otpKey }));
      await checkRequestActionSuccess(action);
      await dispatch(push(baseUrl));
      await dispatch(getIreneDashboard({ propertyId }));
      if (isOtpTypePaymentPlan) {
        return dispatch(
          showPageNotification(PAYMENT_PLAN_CANCEL_SUCCESS, { tenantFirstName, paymentPlanInstallmentTitle }),
        );
      } else {
        return dispatch(showPageNotification(ONE_TIME_PAYMENT_CANCEL_SUCCESS, { tenantFirstName }));
      }
    } catch (err) {
      logError({ error: err });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  goToCancelMoveInCostsModal: async ({ term, rentTermsKey }: EditMoveInCostsInterface): Promise<void> => {
    const payload = {
      term,
    };
    await dispatch(closeNotification());
    await dispatch(saveMoveInCostsSettings({ propertyId, ...payload }));
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.cancelMoveInCostsRequest,
        search: `rentTermsKey=${rentTermsKey}`,
      }),
    );
  },
  remindTenantToPayMIC: async ({ tenantEmail }: Record<string, any>): Promise<any> => {
    try {
      const result = await dispatch(
        sendNotification({
          propertyId,
          notificationType: 'rachelMoveInCostsReminder',
        }),
      );
      await checkRequestActionSuccess(result);
      await dispatch(showPageNotification(REMIND_RENTER_SUCCESS, { tenantEmail }));
      window.scrollTo({ behavior: 'smooth', top: 0 });
    } catch (error) {
      await dispatch(showPageNotification(DEFAULT_ERROR));
      window.scrollTo({ behavior: 'smooth', top: 0 });
      logError({ error });
      return { error: true };
    }
  },
  remindTenantToPayOneTimePayment: async ({ tenantEmail, requestKey }: Record<string, any>): Promise<any> => {
    try {
      const result = await dispatch(
        sendNotification({
          propertyId,
          notificationType: 'rachelOneTimePaymentRequestedByIrene',
          data: JSON.stringify([requestKey]),
        }),
      );
      await checkRequestActionSuccess(result);
      await dispatch(showPageNotification(REMIND_RENTER_SUCCESS, { tenantEmail }));
      window.scrollTo({ behavior: 'smooth', top: 0 });
    } catch (error) {
      await dispatch(showPageNotification(DEFAULT_ERROR));
      window.scrollTo({ behavior: 'smooth', top: 0 });
      logError({ error });
      return { error: true };
    }
  },
  resendInviteToRenter: async ({ tenantEmail }: Record<string, any>): Promise<any> => {
    try {
      const result = await dispatch(
        sendNotification({
          propertyId,
          notificationType: 'rachelPaymentInvite',
        }),
      );
      await checkRequestActionSuccess(result);
      await dispatch(showPageNotification(INVITE_RENTER_SUCCESS, { tenantEmail }));
      window.scrollTo({ behavior: 'smooth', top: 0 });
    } catch (error) {
      await dispatch(showPageNotification(DEFAULT_ERROR));
      window.scrollTo({ behavior: 'smooth', top: 0 });
      logError({ error });
      return { error: true };
    }
  },
  requestRemindRenterPayRent: async ({ tenantEmail }: Record<string, any>): Promise<any> => {
    try {
      const result = await dispatch(
        sendNotification({
          propertyId,
          notificationType: 'rachelPaymentOverdue',
        }),
      );
      await checkRequestActionSuccess(result);
      await dispatch(showPageNotification(REMIND_RENTER_SUCCESS, { tenantEmail }));
    } catch (error) {
      await dispatch(showPageNotification(DEFAULT_ERROR));
      logError({ error, context: 'requestRemindRenterPayRent' });
      return { error: true };
    }
  },
  goToSetupPaymentPlan: async (dismissibleActions): Promise<any> => {
    await dispatch(closeNotification());
    return dismissibleActions[DismissibleActionConstants.PAYMENT_PLAN_INSTRUCTIONS]
      ? dispatch(push(baseUrl + paths.dashboard.howToSetupPaymentPlan))
      : dispatch(push(baseUrl + paths.dashboard.PaymentPlanInstallmentRequests));
  },
  trackValidationError: (data): void => dispatch(trackValidationError(data)),
});
const mergeProps = (stateProps, dispatchProps) => {
  const { dismissibleActions } = stateProps;
  return {
    ...stateProps,
    ...dispatchProps,
    goToSetupPaymentPlan: () => dispatchProps.goToSetupPaymentPlan(dismissibleActions),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(LedgerSection);
